import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getSharePreview, SharePreviewData } from '../api';
import { tosAccelerate } from '../utils/tosAccelerate';
import { useTranslation } from 'react-i18next';

export default function SharePreview() {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation('profile');
  const [data, setData] = useState<SharePreviewData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // 双缓冲播放器状态
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeBuffer, setActiveBuffer] = useState<'A' | 'B'>('A');
  const videoRefA = useRef<HTMLVideoElement>(null);
  const videoRefB = useRef<HTMLVideoElement>(null);
  const activeBufferRef = useRef<'A' | 'B'>('A');
  const [, setInitialReady] = useState(false);
  const [, setSwitchLoading] = useState(false);
  const nextPreloadedUrlRef = useRef('');
  const thumbnailStripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!code) return;
    getSharePreview(code)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [code]);

  const shots = useMemo(() => data?.shots || [], [data?.shots]);

  // 播放结束：自动下一个
  const handleEnded = useCallback(() => {
    if (currentIndex < shots.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, shots.length]);

  // 首次加载第一个视频
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    if (shots.length === 0 || initialLoadedRef.current) return;
    const url = shots[0]?.videoURL;
    if (!url) return;
    let cancelled = false;
    const init = () => {
      if (cancelled || initialLoadedRef.current) return;
      const player = videoRefA.current;
      if (!player) return;
      initialLoadedRef.current = true;
      player.src = tosAccelerate(url);
      player.load();
      const onReady = () => {
        setInitialReady(true);
        if (shots.length > 1 && videoRefB.current) {
          videoRefB.current.src = tosAccelerate(shots[1].videoURL);
          videoRefB.current.load();
          nextPreloadedUrlRef.current = shots[1].videoURL;
        }
      };
      if (player.readyState >= 3) onReady();
      else {
        player.addEventListener('canplay', onReady, { once: true });
        player.addEventListener('error', () => { setInitialReady(true); }, { once: true });
      }
    };
    requestAnimationFrame(init);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shots]);

  // 切换分镜
  const prevIndexRef = useRef(0);
  useEffect(() => {
    if (shots.length === 0) return;
    if (currentIndex === prevIndexRef.current) return;
    prevIndexRef.current = currentIndex;

    const shot = shots[currentIndex];
    if (!shot) return;
    const url = shot.videoURL;

    const curActive = activeBufferRef.current;
    const front = curActive === 'A' ? videoRefA.current : videoRefB.current;
    const back = curActive === 'A' ? videoRefB.current : videoRefA.current;
    if (!front || !back) return;

    const doSwitch = () => {
      const newActive = curActive === 'A' ? 'B' : 'A';
      setActiveBuffer(newActive);
      activeBufferRef.current = newActive;
      back.play().catch(() => {});
      front.pause();
      setSwitchLoading(false);

      // 预加载下一个
      const nextIdx = currentIndex + 1;
      if (nextIdx < shots.length) {
        const nextUrl = shots[nextIdx].videoURL;
        if (nextUrl && nextUrl !== nextPreloadedUrlRef.current) {
          front.src = tosAccelerate(nextUrl);
          front.load();
          nextPreloadedUrlRef.current = nextUrl;
        }
      }
    };

    if (url === nextPreloadedUrlRef.current && back.readyState >= 3) {
      doSwitch();
    } else {
      setSwitchLoading(true);
      back.src = tosAccelerate(url);
      back.load();
      nextPreloadedUrlRef.current = '';
      back.addEventListener('canplay', () => { doSwitch(); }, { once: true });
    }

    // 滚动缩略图
    if (thumbnailStripRef.current) {
      const child = thumbnailStripRef.current.children[currentIndex] as HTMLElement;
      if (child) child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, shots]);

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={{ color: '#999', marginTop: '1rem' }}>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.center}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {error.includes('expired') ? '\u231B' : error.includes('view limit') ? '\u{1F6AB}' : '\u26A0\uFE0F'}
          </div>
          <h2 style={{ color: '#333', margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
            {error.includes('expired') ? t('sharePreview.linkExpired') : error.includes('view limit') ? t('sharePreview.viewLimitReached') : t('sharePreview.cannotAccess')}
          </h2>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data || shots.length === 0) {
    return (
      <div style={styles.center}>
        <p style={{ color: '#888' }}>No videos available</p>
      </div>
    );
  }

  const totalDuration = shots.reduce((sum, s) => sum + (s.duration || 0), 0);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🎬</span>
          <span style={{ fontWeight: 600, fontSize: '1rem', color: '#fff' }}>{data.episodeName}</span>
        </div>
        <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}>
          {shots.length} shots · {totalDuration}s
        </span>
      </div>

      {/* 双缓冲视频播放器 */}
      <div style={styles.playerContainer}>
        <div style={{ position: 'relative', width: '100%', background: '#000' }}>
          <video
            ref={videoRefA}
            controls
            playsInline
            onEnded={handleEnded}
            style={{
              width: '100%', display: 'block',
              position: activeBuffer === 'A' ? 'relative' : 'absolute',
              top: 0, left: 0,
              zIndex: activeBuffer === 'A' ? 2 : 1,
              opacity: activeBuffer === 'A' ? 1 : 0,
              pointerEvents: activeBuffer === 'A' ? 'auto' : 'none',
            }}
          />
          <video
            ref={videoRefB}
            controls
            playsInline
            onEnded={handleEnded}
            style={{
              width: '100%', display: 'block',
              position: activeBuffer === 'B' ? 'relative' : 'absolute',
              top: 0, left: 0,
              zIndex: activeBuffer === 'B' ? 2 : 1,
              opacity: activeBuffer === 'B' ? 1 : 0,
              pointerEvents: activeBuffer === 'B' ? 'auto' : 'none',
            }}
          />
        </div>
      </div>

      {/* 缩略图条 */}
      <div ref={thumbnailStripRef} style={styles.thumbnailStrip}>
        {shots.map((shot, idx) => (
          <div
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            style={{
              flexShrink: 0,
              width: '90px',
              border: idx === currentIndex ? '2px solid #2563eb' : '1px solid #333',
              borderRadius: '6px',
              overflow: 'hidden',
              cursor: 'pointer',
              background: idx === currentIndex ? 'rgba(37,99,235,0.15)' : '#1a1a1a',
              transition: 'all 0.15s ease',
            }}
          >
            <video
              src={tosAccelerate(shot.videoURL)}
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '50px', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
            />
            <div style={{
              padding: '2px 4px',
              fontSize: '0.7rem',
              textAlign: 'center',
              fontWeight: idx === currentIndex ? 600 : 400,
              color: idx === currentIndex ? '#2563eb' : '#aaa',
            }}>
              #{shot.index} · {shot.duration}s
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#111',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '640px',
    margin: '0 auto',
  },
  center: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#111',
    color: '#fff',
  },
  header: {
    padding: '0.75rem 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #222',
  },
  playerContainer: {
    width: '100%',
    background: '#000',
  },
  loadingOverlay: {
    position: 'absolute' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.6)',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #fff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  thumbnailStrip: {
    padding: '0.75rem',
    display: 'flex',
    gap: '0.5rem',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as any,
  },
};
